<script lang="ts">
  import { format, addDays, addMonths, addYears, parseISO } from 'date-fns';
  import { userService } from '$lib/services/user.service';
  import { toasts } from '$lib/stores/toastStores';
  import { dialogStates } from '$lib/stores/dialogStores';
  import { user } from '$lib/stores';
  import { get } from 'svelte/store';

  let loading = false;
  let selectedPeriod: string = '7_DAYS';

  // Define exclusion periods with their corresponding days value
  const exclusionPeriods = [
    { value: '7_DAYS', label: '7 Days', days: 7 },
    { value: '30_DAYS', label: '30 Days', days: 30 },
    { value: '90_DAYS', label: '90 Days', days: 90 },
    { value: '180_DAYS', label: '180 Days', days: 180 },
    { value: '1_YEAR', label: '1 Year', days: 365 },
    { value: '5_YEARS', label: '5 Years', days: 1825 },
    { value: 'PERMANENT', label: 'Permanent', days: -1 } // Special value for permanent
  ];

  function getExclusionEndDate(period: string): string {
    const now = new Date();

    switch (period) {
      case '7_DAYS':
        return format(addDays(now, 7), 'MMM dd, yyyy');
      case '30_DAYS':
        return format(addDays(now, 30), 'MMM dd, yyyy');
      case '90_DAYS':
        return format(addDays(now, 90), 'MMM dd, yyyy');
      case '180_DAYS':
        return format(addDays(now, 180), 'MMM dd, yyyy');
      case '1_YEAR':
        return format(addYears(now, 1), 'MMM dd, yyyy');
      case '5_YEARS':
        return format(addYears(now, 5), 'MMM dd, yyyy');
      case 'PERMANENT':
        return 'Permanent';
      default:
        return 'Invalid period';
    }
  }

  // Get the days value for the selected period
  function getDaysForPeriod(periodValue: string): number {
    const period = exclusionPeriods.find(p => p.value === periodValue);
    return period ? period.days : 1; // Default to 1 day if not found
  }

  async function handleSubmit() {
    if (!selectedPeriod) {
      toasts.send('Please select an exclusion period', 'error');
      return;
    }

    try {
      loading = true;

      // Get the days value for the selected period
      const exclusionDays = getDaysForPeriod(selectedPeriod);

      // Use the format expected by the API (exclusionDays instead of period)
      const response = await userService.setSelfExclusion({
        exclusionDays: exclusionDays
      });

      if (response.error) {
        toasts.send(response.error, 'error');
        return;
      }

      toasts.send('Self-exclusion set successfully', 'success');
      dialogStates.update(state => ({ ...state, SelfExcludeModal: false }));

      // Update user state if needed
      if (get(user)) {
        user.set({
          ...get(user),
          selfExcluded: true,
          selfExclusionExpiresAt: response.data.expiresAt
        });
      }

      // Reload the page to reflect changes
      window.location.reload();
    } catch (error) {
      console.error('Self-exclusion error:', error);
      toasts.send('Failed to set self-exclusion', 'error');
    } finally {
      loading = false;
    }
  }
</script>

<div class="p-6">
  <h2 class="text-xl font-semibold mb-6">Self-Exclusion</h2>

  <div class="space-y-6">
    <div>
      <p class="text-gray-300 mb-4">
        Self-exclusion allows you to take a break from gambling. During this period, you won't be able to access your account or place any bets.
      </p>

      <div class="bg-yellow-900/20 border border-yellow-700/20 rounded-lg p-4 mb-6">
        <p class="text-yellow-500 text-sm">
          Warning: This action cannot be reversed until the selected period expires.
        </p>
      </div>
    </div>

    <div class="space-y-4">
      <label class="block">
        <span class="text-gray-300">Select Exclusion Period</span>
        <select
                bind:value={selectedPeriod}
                class="mt-1 block w-full rounded-md bg-gray-800 border-gray-700 text-gray-300 focus:border-primary focus:ring-primary"
        >
          {#each exclusionPeriods as period}
            <option value={period.value}>{period.label}</option>
          {/each}
        </select>
      </label>

      {#if selectedPeriod !== 'PERMANENT'}
        <div class="bg-gray-800/50 rounded-lg p-4">
          <p class="text-sm text-gray-400">Your account will be blocked until:</p>
          <p class="text-lg font-medium">{getExclusionEndDate(selectedPeriod)}</p>
        </div>
      {/if}
    </div>

    <div class="flex justify-end space-x-4">
      <button
              type="button"
              class="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
              on:click={() => dialogStates.update(state => ({ ...state, SelfExcludeModal: false }))}
      >
        Cancel
      </button>
      <button
              type="button"
              class="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 transition-colors disabled:opacity-50"
              on:click={handleSubmit}
              disabled={loading}
      >
        {loading ? 'Processing...' : 'Confirm Self-Exclusion'}
      </button>
    </div>
  </div>
</div>